import React, { useState, useEffect } from 'react';
import { Collapse, Table, Typography, Space } from 'antd';
import { DownOutlined, RightOutlined, DollarOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * FinOpsTableComponent for displaying agent performance metrics
 * 
 * @param {Object} props
 * @param {Object} props.data - The table data containing report_data and period
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const FinOpsTableComponent = ({
  data,
  title = "Agent Performance Report",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      const unregister = registerComponent(refreshComponent);
      return unregister;
    }
  }, [registerComponent, uuid]);

  // Extract data
  const reportData = data?.report_data || [];
  const period = data?.period || 'Unknown period';

  // Define table columns
  const columns = [
    {
      title: 'Agent Name',
      dataIndex: 'agent_name',
      key: 'agent_name',
      fixed: 'left',
      width: 200,
      sorter: (a, b) => a.agent_name.localeCompare(b.agent_name),
      render: (text) => (
        <Text strong style={{ color: '#6200EE' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Total Requests',
      dataIndex: 'total_requests',
      key: 'total_requests',
      width: 120,
      sorter: (a, b) => a.total_requests - b.total_requests,
      align: 'right'
    },
    {
      title: 'Input Tokens',
      dataIndex: 'input_tokens',
      key: 'input_tokens',
      width: 120,
      align: 'right'
    },
    {
      title: 'Output Tokens',
      dataIndex: 'output_tokens',
      key: 'output_tokens',
      width: 120,
      align: 'right'
    },
    {
      title: 'Total Tokens',
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      width: 120,
      align: 'right'
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 120,
      sorter: (a, b) => {
        const aVal = parseFloat(a.total_cost.replace('$', ''));
        const bVal = parseFloat(b.total_cost.replace('$', ''));
        return aVal - bVal;
      },
      align: 'right',
      render: (text) => (
        <Text style={{ fontWeight: '600', color: '#52c41a' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Avg Cost/Request',
      dataIndex: 'avg_cost_per_request',
      key: 'avg_cost_per_request',
      width: 140,
      align: 'right',
      render: (text) => (
        <Text style={{ color: '#722ed1' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Online Cost',
      dataIndex: 'online_cost',
      key: 'online_cost',
      width: 120,
      align: 'right'
    },
    {
      title: 'Background Cost',
      dataIndex: 'background_cost',
      key: 'background_cost',
      width: 140,
      align: 'right'
    }
  ];

  // Add row keys for table
  const dataSource = reportData.map((row, index) => ({
    ...row,
    key: row.agent_name || index
  }));

  // Calculate summary statistics
  const totalRequests = reportData.reduce((sum, row) => sum + row.total_requests, 0);
  const totalCostSum = reportData.reduce((sum, row) => {
    const cost = parseFloat(row.total_cost.replace('$', ''));
    return sum + cost;
  }, 0);

  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DollarOutlined style={{ color: '#6200EE' }} />
                {title}
              </div>
            ),
            children: (
              <div>
                {/* Summary Info */}
                <div style={{ 
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef'
                }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text style={{ fontSize: '14px', color: '#6c757d' }}>
                      <strong>Period:</strong> {period}
                    </Text>
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                      <Text style={{ fontSize: '14px' }}>
                        <strong>Total Agents:</strong> {reportData.length}
                      </Text>
                      <Text style={{ fontSize: '14px' }}>
                        <strong>Total Requests:</strong> {totalRequests.toLocaleString()}
                      </Text>
                      <Text style={{ fontSize: '14px', color: '#52c41a' }}>
                        <strong>Total Cost:</strong> ${totalCostSum.toFixed(4)}
                      </Text>
                    </div>
                  </Space>
                </div>

                {/* Main Table */}
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  scroll={{ x: 1200 }}
                  size="small"
                  bordered
                  style={{
                    backgroundColor: 'white'
                  }}
                  rowClassName={(record, index) => 
                    index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
                  }
                />

                {/* Additional Info */}
                <div style={{ 
                  marginTop: '16px',
                  fontSize: '12px',
                  color: '#8c8c8c',
                  textAlign: 'center'
                }}>
                  Data shows cumulative usage and costs for the selected period. 
                  Costs are calculated based on model-specific pricing and operation modes.
                </div>
              </div>
            )
          }
        ]}
      />
      
      <style jsx>{`
        .table-row-even {
          background-color: #fafafa;
        }
        .table-row-odd {
          background-color: white;
        }
        .table-row-even:hover,
        .table-row-odd:hover {
          background-color: #e6f7ff !important;
        }
      `}</style>
    </div>
  );
};

export default FinOpsTableComponent;